import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ArrowUp, ArrowDown, ArrowUpDown, Columns3, GripVertical, Check, RotateCcw } from "lucide-react";

/**
 * Bouton "Colonnes" avec :
 * - Cases à cocher pour masquer/afficher chaque colonne
 * - Drag-and-drop (HTML5 natif) pour réordonner
 * - Bouton "Réinitialiser" l'ordre
 *
 * Props :
 * - columns : ordre d'origine (défaut).
 * - orderedColumns : colonnes dans l'ordre utilisateur actuel (issu du hook).
 * - isVisible, toggleVisibility : contrôle de la visibilité.
 * - moveColumn(fromKey, toKey) : déplace une colonne.
 * - resetOrder() : restaure l'ordre par défaut.
 */
export const ColumnsToggle = ({
  columns,
  orderedColumns,
  isVisible,
  toggleVisibility,
  moveColumn,
  resetOrder,
  testId = "columns-toggle",
}) => {
  const [draggedKey, setDraggedKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  // Fallback si le parent ne fournit pas orderedColumns
  const list = orderedColumns || columns;

  const handleDragStart = (e, key) => {
    setDraggedKey(key);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", key);
    } catch {
      // Safari strict mode
    }
  };

  const handleDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overKey !== key) setOverKey(key);
  };

  const handleDrop = (e, key) => {
    e.preventDefault();
    const from = draggedKey || e.dataTransfer.getData("text/plain");
    if (from && moveColumn) moveColumn(from, key);
    setDraggedKey(null);
    setOverKey(null);
  };

  const handleDragEnd = () => {
    setDraggedKey(null);
    setOverKey(null);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid={testId}>
          <Columns3 className="h-4 w-4 mr-2" />
          Colonnes
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-1">
        <div className="flex items-center justify-between px-2 pt-1 pb-0.5">
          <DropdownMenuLabel className="p-0 text-xs">Colonnes</DropdownMenuLabel>
          {resetOrder && (
            <button
              type="button"
              onClick={resetOrder}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition-colors"
              data-testid={`${testId}-reset-order`}
              title="Rétablir l'ordre par défaut"
            >
              <RotateCcw className="h-3 w-3" /> Réinitialiser
            </button>
          )}
        </div>
        <p className="px-2 pb-1 text-[10px] text-muted-foreground">
          Glissez-déposez pour réordonner · cochez pour afficher
        </p>
        <DropdownMenuSeparator />
        <ul className="py-1">
          {list.map((col) => {
            const checked = isVisible(col.key);
            const isDragging = draggedKey === col.key;
            const isOver = overKey === col.key && draggedKey && draggedKey !== col.key;
            return (
              <li
                key={col.key}
                draggable={!col.always}
                onDragStart={(e) => handleDragStart(e, col.key)}
                onDragOver={(e) => handleDragOver(e, col.key)}
                onDragLeave={() => setOverKey(null)}
                onDrop={(e) => handleDrop(e, col.key)}
                onDragEnd={handleDragEnd}
                className={[
                  "group flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-default select-none",
                  isDragging ? "opacity-40" : "",
                  isOver ? "bg-primary/10 ring-1 ring-primary/40" : "hover:bg-accent",
                ].join(" ")}
                data-testid={`${testId}-item-${col.key}`}
              >
                <GripVertical
                  className={`h-3.5 w-3.5 ${col.always ? "opacity-20" : "opacity-40 cursor-grab active:cursor-grabbing"}`}
                  data-testid={`${testId}-handle-${col.key}`}
                />
                <button
                  type="button"
                  onClick={() => toggleVisibility(col.key)}
                  disabled={col.always}
                  className="flex items-center gap-2 flex-1 text-left disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid={`${testId}-${col.key}`}
                >
                  <span
                    className={[
                      "inline-flex items-center justify-center h-4 w-4 rounded border text-primary-foreground",
                      checked ? "bg-primary border-primary" : "bg-transparent border-muted-foreground/40",
                    ].join(" ")}
                  >
                    {checked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex-1">{col.label}</span>
                  {col.always && (
                    <span className="text-[10px] text-muted-foreground">(requis)</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Bouton de tri seul (sans th/TableHead), utilisable dans n'importe quel
 * conteneur d'en-tête de table (ex: `<TableHead>` de Shadcn).
 */
export const SortButton = ({ columnKey, label, sortArrow, handleSort, children }) => {
  const state = sortArrow(columnKey);
  const Icon = state === "asc" ? ArrowUp : state === "desc" ? ArrowDown : ArrowUpDown;
  const isActive = state !== "none";
  return (
    <button
      type="button"
      onClick={() => handleSort(columnKey)}
      className={`inline-flex items-center gap-1 font-inherit text-inherit hover:text-primary transition-colors select-none ${
        isActive ? "text-primary" : ""
      }`}
      data-testid={`sort-${columnKey}`}
      title={`Trier par ${label}`}
    >
      <span>{children ?? label}</span>
      <Icon className={`h-3.5 w-3.5 ${isActive ? "opacity-100" : "opacity-40"}`} />
    </button>
  );
};

/**
 * En-tête de colonne cliquable pour déclencher un tri.
 * Affiche une flèche selon l'état du tri.
 */
export const SortableTh = ({
  columnKey,
  label,
  sortable = true,
  sortArrow,
  handleSort,
  className = "",
  children,
}) => {
  if (!sortable) {
    return <th className={className}>{children ?? label}</th>;
  }
  return (
    <th className={className}>
      <SortButton columnKey={columnKey} label={label} sortArrow={sortArrow} handleSort={handleSort}>
        {children ?? label}
      </SortButton>
    </th>
  );
};
